import { Link } from "react-router-dom";
import { ScrollSection } from "@/components/main-page/ScrollSection";
import { useArticles, getCategoryColor } from "@/lib/insights";

function ArrowIcon() {
  return (
    <span className="w-8 h-8 border border-[var(--color-border)] flex items-center justify-center flex-shrink-0 text-[var(--color-fg-tertiary)] group-hover:text-[var(--color-fg)] group-hover:border-[var(--color-fg-secondary)] transition-colors">
      <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 8h10M9 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

export function InsightsSection() {
  const articles = useArticles().slice(0, 3);
  const featured = articles[0];
  const rest = articles.slice(1);

  if (!featured) return null;

  return (
    <ScrollSection id="insights" title="Latest insights" subtitle="Thinking and updates from the COSX team.">
      <div className="grid md:grid-cols-2 gap-4 md:h-[420px]">
        {/* Featured — left */}
        <Link
          to={`/insights/${featured.slug}`}
          className="group border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden flex flex-col h-full hover:border-[var(--color-fg-secondary)] transition-colors"
        >
          <div className="aspect-[4/3] md:aspect-auto md:flex-1 bg-[var(--color-bg-secondary)] overflow-hidden">
            {featured.thumbnail && <img src={featured.thumbnail} alt={featured.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" />}
          </div>
          <div className="p-6 flex items-end justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className={`text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-2 py-0.5 border ${getCategoryColor(featured.category)}`}>
                  {featured.category}
                </span>
                <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
                  {new Date(featured.date).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                </span>
              </div>
              <h3 className="text-lg font-medium leading-snug">{featured.title}</h3>
            </div>
            <ArrowIcon />
          </div>
        </Link>

        {/* Right — stacked cards */}
        <div className="flex flex-col gap-4 h-full">
          {rest.map((article) => (
            <Link
              key={article.slug}
              to={`/insights/${article.slug}`}
              className="group border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden flex flex-col md:flex-row flex-1 min-h-0 hover:border-[var(--color-fg-secondary)] transition-colors"
            >
              <div className="aspect-[4/3] md:aspect-auto md:w-[55%] bg-[var(--color-bg-secondary)] flex-shrink-0 overflow-hidden">
                {article.thumbnail && <img src={article.thumbnail} alt={article.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" />}
              </div>
              <div className="flex-1 p-5 flex flex-col">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`text-[10px] font-[family-name:var(--font-mono)] tracking-widest uppercase px-2 py-0.5 border ${getCategoryColor(article.category)}`}>
                    {article.category}
                  </span>
                  <span className="text-[10px] font-[family-name:var(--font-mono)] text-[var(--color-fg-tertiary)]">
                    {new Date(article.date).toLocaleDateString("en-GB", { month: "short", year: "numeric" })}
                  </span>
                </div>
                <div className="mt-auto flex items-end justify-between gap-3">
                  <h3 className="text-base font-medium leading-snug">{article.title}</h3>
                  <ArrowIcon />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <div className="mt-8">
        <Link
          to="/insights"
          className="text-sm font-[family-name:var(--font-mono)] text-[var(--color-fg-secondary)] hover:text-[var(--color-fg)] transition-colors"
        >
          All insights →
        </Link>
      </div>
    </ScrollSection>
  );
}
